import { render } from 'preact';

import merge from 'deepmerge';

import { FormManager } from '@/features/manager';
import {
	getCookie,
	setCookie,
	getUrlParam,
	getStrParam,
	isArray,
	nodeListToArray,
	triggerEvent,
	log,
	rnd
} from '@/utils';

if (process.env.NODE_ENV === 'development') {
	import('preact/debug');
}

const disablePostFromCookie = getCookie('MopinionDisablePost') == 1;

function getDiv(div, useAll) {
	return useAll
		? nodeListToArray(document.querySelectorAll(div))
		: document.querySelector(div);
}

function tryDiv(div, useAll = false) {
	try {
		if (getDiv(div, useAll)) {
			return getDiv(div, useAll);
		} else {
			return getDiv('#' + div, useAll);
		}
	} catch (e) {
		return null;
	}
}

function getContainerDiv({ container, useAll, existingContainer, baseContainer }) {
	if (!container) {
		return existingContainer ?? baseContainer;
	}

	if (container instanceof Node) {
		return container;
	}

	if (container instanceof NodeList) {
		return nodeListToArray(container);
	}

	if (
		typeof container === 'string' &&
		container.length &&
		container.indexOf(',') === -1
	) {
		return tryDiv(container, useAll);
	}

	if (typeof container === 'string' && container.indexOf(',') > -1) {
		return container.split(',').map(n => tryDiv(n));
	}

	return getDiv('#surveyContent:not(.mopinion-survey-content)');
}

function getDomain({ domain, existingDomain, baseDomain }) {
	if (domain) {
		return domain;
	}
	if (existingDomain) {
		return existingDomain;
	}
	return baseDomain;
}

let extraProps = {};

function setExtraProps(update) {
	extraProps = merge(extraProps, update, {
		customMerge: key => {
			//clear older set values
			if (key === 'values') {
				return (a, b) => b;
			}
		}
	});
}

export const listeners = {};

const stateToArray = obj => {
	return Object.keys(obj)
		.filter(key => !isNaN(key))
		.map(key => obj[key]);
};

const srv = {
	//main render function
	version: 2.0,
	ALL_KEYS: [],
	managerDiv: null,
	render(options = {}) {
		//Normalize props
		const triggerMethod = options.triggerMethod ?? options.trigger_method;
		const domain = getDomain({
			domain: options.domain,
			existingDomain: srv[options.key]?.domain,
			baseDomain: srv[srv.ALL_KEYS[0]]?.domain
		});

		const props = {
			formKey: options.key,
			domain,
			formOpen:
				options.formOpen ??
				(['proactive', 'exit', 'force'].indexOf(triggerMethod) > -1 || null),
			showButton: options.showButton || triggerMethod === 'passive',
			cookieTime: options.cookie_expire || options.cookieTime || 30,
			triggerMethod,
			forcePost: options.forcePost || false,
			skipToLast: options.skipToLast || false,
			forceType: options.forceType || false,
			forceBreakPoint: options.forceBreakPoint || false,
			forceButton: options.forceButton || false,
			forceHeader: options.forceHeader || false,
			webView: options.webView || false,
			webViewShowHeader: options.webViewShowHeader || false,
			proxyDomain: options.proxyDomain,
			cacheDomain:
				options.proxyCacheDomain ?? options.proxyDomain ?? 'cacheorcheck.mopinion.com',
			customDomain: options.proxyCustomDomain ?? options.proxyDomain ?? domain,
			feedbackDomain:
				options.proxyFeedbackDomain ?? options.proxyDomain ?? 'survey.mopinion.com',
			metricsDomain:
				options.proxyMetricsDomain ?? options.proxyDomain ?? 'metrics.mopinion.com',
			fontsDomain:
				options.proxyFontsDomain ?? options.proxyDomain ?? 'fonts.mopinion.com',
			isCompleted: options.isCompleted || false,
			feedbackId: options.feedbackId || null,
			disablePost: disablePostFromCookie || options.disablePost || false,
			noCache: options.noCache || false,
			instance: srv[options.key]?.instance ?? rnd(),
			refresh: options.refresh ? rnd() : null,
			cssDomain: options.proxyCssDomain ?? options.proxyDomain ?? 'collect.mopinion.com'
		};

		if (props.formOpen !== null) {
			props.openAction = `form_open-${rnd()}`;
		}

		if (!props.formKey) {
			throw new Error('formKey required');
		}
		if (!srv.hasOwnProperty(props.formKey)) {
			srv[props.formKey] = {};
			srv.ALL_KEYS = [...srv.ALL_KEYS, props.formKey];
		}

		//Keep show button if it was true and hasnt been forced off
		if (!options.hasOwnProperty('showButton') && srv[props.formKey].showButton) {
			props.showButton = true;
		}

		//supports single id as string e.g 'surveyContent'
		//supports csv e.g 'surveyContent, surveyContent2'
		//supports arrays e.g ['surveyContent', 'surveyContent2']
		//if array or csv -> render multiple instances of same form in different divs
		//supports all valid css selectors now
		//node and nodelists
		const container = options.container ?? options.divName;

		const divName = getContainerDiv({
			container,
			useAll: options.useQuerySelectorAll,
			existingContainer: srv[props.formKey].divName,
			baseContainer: srv[srv.ALL_KEYS[0]]?.divName
		});

		props.divName = divName;
		props.isMultiple = isArray(divName);
		srv[props.formKey] = props;

		log('Props', props);
		log('Options', options);
		log('Render div', srv[props.formKey].renderDiv);
		return this.renderForms();
	},
	renderForms() {
		if (!srv.managerDiv) {
			const div = document.createElement('div');
			document.body.appendChild(div);
			srv.managerDiv = div;
		}

		const forms = srv.ALL_KEYS.map(key => {
			return {
				...srv[key],
				extra: extraProps[key]
			};
		});
		render(<FormManager forms={forms} />, srv.managerDiv);
	},
	open(options = {}) {
		return srv.render(options);
	},
	isOpen(formKey) {
		return srv[formKey]?._formOpen && srv[formKey]?._formType !== 'embed';
	},
	//API functions -> trigger form functions on already rendered forms
	openModal(force = true, formKey = srv.ALL_KEYS[0]) {
		try {
			srv.open({
				key: formKey,
				formOpen: true
			});
		} catch (e) {}
	},
	closeModal(formKey = srv.ALL_KEYS[0]) {
		try {
			srv.open({
				key: formKey,
				formOpen: false
			});
		} catch (e) {}
	},
	send(skipToLast = false, formKey = '') {
		try {
			if (!srv[formKey].isMultiple) {
				srv.manager[formKey]._forcePost(skipToLast);
			} else {
				srv[formKey].divName.forEach((d, i) =>
					srv.manager[formKey][i]._forcePost(skipToLast)
				);
			}
		} catch (e) {}
	},
	clearForm(formKey = '') {
		try {
			delete srv[formKey];
			const index = srv.ALL_KEYS.indexOf(formKey);
			if (index !== -1) {
				srv.ALL_KEYS.splice(srv.ALL_KEYS.indexOf(formKey), 1);
				this.renderForms();
			}
		} catch (e) {}
	},
	setPage(formKey = '', page) {
		try {
			if (typeof page !== 'number') {
				return;
			}
			setExtraProps({
				[formKey]: {
					page
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey,
				domain: srv.ALL_KEYS[0].domain
			});
		} catch (e) {}
	},
	getFormConfig(formKey = '') {
		try {
			return srv[formKey]._config;
		} catch (e) {}
	},
	getFormState(formKey) {
		try {
			if (srv[formKey].isMultiple) {
				return srv[formKey]._multiple.map(data => stateToArray(data._state));
			}

			return stateToArray(srv[formKey]._state);
		} catch (e) {}
	},
	setValue(formKey = '', field = '', value = '') {
		try {
			setExtraProps({
				[formKey]: {
					values: {
						[field]: value
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setBlock(formKey = '', id, obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						blocks: {
							[id]: obj
						}
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setBlockProperties(formKey = '', id, obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						blocks: {
							[id]: {
								properties: obj
							}
						}
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setBlockInnerElement(formKey = '', { id, elementKey }, obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						blocks: {
							[id]: {
								properties: {
									elements: obj
								}
							}
						}
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setProperties(formKey = '', obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						properties: obj
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setText(formKey = '', obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						text: obj
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setErrors(formKey = '', obj) {
		try {
			setExtraProps({
				[formKey]: {
					config: {
						text: {
							errors: obj
						}
					}
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	setLanguage(formKey = '', language) {
		try {
			setExtraProps({
				[formKey]: {
					language
				}
			});
			if (!srv[formKey]) {
				return;
			}
			srv.render({
				key: formKey
			});
		} catch (e) {}
	},
	//Helpers functions
	setCookie(name = '', value, days) {
		return setCookie(name, value, days);
	},
	getCookie(name = '') {
		return getCookie(name);
	},
	getUrlParam(name = '') {
		return getUrlParam(name);
	},
	getStrParam(name = '', str = document.location.href) {
		return getStrParam(name, str);
	},
	//legacy naming
	getParameterByName(name = '', str = document.location.href) {
		return getStrParam(name, str);
	},
	triggerEvent(data = '') {
		return triggerEvent(data);
	},
	addEventListener(event, fn) {
		if (!listeners[event]) {
			listeners[event] = [];
		}
		listeners[event].push(fn);
	},
	removeEventListener(event, fn) {
		try {
			listeners[event] = listeners[event].filter(ev => {
				return ev.toString() !== fn.toString();
			});
		} catch (e) {}
	},
	reset(formKey) {
		if (!srv[formKey]) {
			return;
		}

		srv[formKey].instance = rnd();
		this.renderForms();
	}
};

if (typeof window !== 'undefined') {
	window.srv = window.srv || srv;
	window.mopinionSurvey = window.mopinionSurvey || srv;
}

export default srv;
